import { useState } from 'react';
import PageBanner from '../components/PageBanner';
import useInView from '../hooks/useInView';
import axios from '../api/axios';
import SEO from '../components/SEO';

const CONTACT_URL = '/contact';

const Contact = () => {
    const [formRef, formVisible] = useInView();

    const [form, setForm] = useState({
        firstname: '', lastname: '', email: '', phone: '', subject: '', message: '',
    });
    const [status, setStatus] = useState(null); // 'sending' | 'success' | 'error'
    const [errMsg, setErrMsg] = useState('');

    const handleChange = (e) => {
        setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
        setStatus(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setStatus('sending');
        setErrMsg('');
        try {
            await axios.post(CONTACT_URL, form);
            setStatus('success');
            setForm({ firstname: '', lastname: '', email: '', phone: '', subject: '', message: '' });
        } catch (err) {
            setStatus('error');
            setErrMsg(err?.response?.data?.message || 'Something went wrong. Please try again.');
        }
    };

    return (
        <div>
            <SEO
                title="Contact"
                description="Get in touch with Kia Kaha Construction for custom home builds, renovations, and commercial projects in Squamish, Whistler, Pemberton, and the Sea to Sky corridor."
                keywords="contact contractor Squamish, construction quote Whistler, home builder contact Sea to Sky, Pemberton contractor"
            />
            <PageBanner
                image="images/layton-drive-2.jpg"
                subtitle="Reach Out"
                title="Get In Touch"
                position="bg-[center_70%]"
            />

            <section ref={formRef} className="section max-w-7xl mx-auto">
                <div className="grid grid-cols-1 lg:grid-cols-5 gap-16">

                    {/* ── Contact info ── */}
                    <div
                        className={`lg:col-span-2 transition-all duration-700
              ${formVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}
                    >
                        <p className="font-sans text-xs tracking-[0.3em] uppercase text-jade-500 mb-4">
                            Contact
                        </p>
                        <h2 className="font-serif text-4xl font-light text-stone-800 mb-2 leading-tight">
                            Let's Build Something
                        </h2>
                        <div className="divider-left" />

                        <div className="space-y-8 mt-8">
                            <div>
                                <p className="font-sans text-xs tracking-widest uppercase text-stone-400 mb-2">Email</p>
                                <a
                                    href="mailto:kiakahaconstruction@gmail.com"
                                    className="font-sans text-stone-600 hover:text-jade-500 transition-colors duration-200"
                                >
                                    kiakahaconstruction@gmail.com
                                </a>
                            </div>
                            <div>
                                <p className="font-sans text-xs tracking-widest uppercase text-stone-400 mb-2">Location</p>
                                <p className="font-sans text-stone-600">Squamish, BC</p>
                            </div>
                        </div>
                    </div>

                    {/* ── Form ── */}
                    <div
                        className={`lg:col-span-3 transition-all duration-700 delay-200
              ${formVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}
                    >
                        <form onSubmit={handleSubmit} className="space-y-6" noValidate>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                <Field label="First Name" name="firstname" value={form.firstname} onChange={handleChange} required />
                                <Field label="Last Name" name="lastname" value={form.lastname} onChange={handleChange} required />
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                <Field label="Email" name="email" type="email" value={form.email} onChange={handleChange} required />
                                <Field label="Phone" name="phone" type="tel" value={form.phone} onChange={handleChange} />
                            </div>
                            <Field label="Subject" name="subject" value={form.subject} onChange={handleChange} />
                            <div>
                                <label className="block font-sans text-xs tracking-widest uppercase text-stone-400 mb-2">
                                    Message <span className="text-jade-500 text-sm">*</span>
                                </label>
                                <textarea
                                    name="message"
                                    value={form.message}
                                    onChange={handleChange}
                                    required
                                    rows={6}
                                    className="w-full border-b border-stone-300 bg-transparent py-3 font-sans text-stone-700
                             placeholder-stone-300 focus:outline-none focus:border-jade-500
                             transition-colors duration-200 resize-none"
                                    placeholder="Tell us about your project..."
                                />
                            </div>

                            {/* Status messages */}
                            {status === 'success' && (
                                <p className="font-sans text-sm text-jade-600">
                                    Message sent — we'll be in touch soon.
                                </p>
                            )}
                            {status === 'error' && (
                                <p className="font-sans text-sm text-red-500">{errMsg}</p>
                            )}

                            <button
                                type="submit"
                                disabled={status === 'sending'}
                                className="btn-jade disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {status === 'sending' ? 'Sending…' : 'Send Message'}
                            </button>
                        </form>
                    </div>
                </div>
            </section>
        </div>
    );
};

// ── Reusable field ──────────────────────────────────────────────────────────
const Field = ({ label, name, type = 'text', value, onChange, required }) => (
    <div>
        <label className="block font-sans text-xs tracking-widest uppercase text-stone-400 mb-2">
            {label} {required && <span className="text-jade-500 text-sm">*</span>}
        </label>
        <input
            type={type}
            name={name}
            value={value}
            onChange={onChange}
            required={required}
            className="w-full border-b border-stone-300 bg-transparent py-3 font-sans text-stone-700
                 placeholder-stone-300 focus:outline-none focus:border-jade-500
                 transition-colors duration-200"
        />
    </div>
);

export default Contact;
